import {
  Grow,
  useMediaQuery,
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Theme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { useState } from "react";

import { MuiInput } from "../MuiInput";

import { MonthRangeCalendar } from "./MonthRangeCalendar";

interface CustomMonthRangePickerProps {
  value?: [Dayjs | null, Dayjs | null];
  onChange?: (range: [Dayjs | null, Dayjs | null]) => void;
  label?: string;
  format?: string;
  error?: boolean;
  helperText?: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  sx?: any;
}

export const MonthRangePicker = ({
  value = [null, null],
  onChange,
  label,
  format: displayFormat = "MMM YYYY",
  error = false,
  helperText = "",
  maxDate,
  minDate,
  sx,
}: CustomMonthRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const start = value[0] || null;
  const end = value[1] || null;
  const open = Boolean(anchorEl);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getValue = () => {
    if (start && end) {
      return `${start.format(displayFormat)} – ${end.format(displayFormat)}`;
    }

    if (start) {
      return `${start.format(displayFormat)} – ...`;
    }

    return "";
  };

  const handleRangeChange = (range: [Dayjs | null, Dayjs | null]) => {
    if (onChange) {
      onChange(range);
    }

    if (range[0] && range[1]) {
      handleClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiInput
        error={error}
        helperText={helperText}
        label={label}
        size="medium"
        InputProps={{ readOnly: true }}
        value={getValue()}
        sx={{
          ...sx,
          "& .MuiOutlinedInput-input": { cursor: "pointer" },
        }}
        onClick={handleClick}
      />
      <Popper
        transition
        anchorEl={anchorEl}
        modifiers={[
          {
            name: "offset",
            options: { offset: isMobile ? [10, -300] : [0, 8] },
          },
        ]}
        open={open}
        placement={isMobile ? "top" : "bottom-start"}
        sx={{ position: "relative", zIndex: 1 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <MonthRangeCalendar
                    maxDate={maxDate}
                    minDate={minDate}
                    value={value}
                    onChange={handleRangeChange}
                  />
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </LocalizationProvider>
  );
};
